import React, { useState, useEffect, useRef } from 'react';
import { Base64TextService } from '../../lib/services/base64TextService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType, CharacterEncoding } from '../../lib/types';
import './Base64TextEncoder.css';

export const Base64TextEncoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [encoding, setEncoding] = useState<CharacterEncoding>(CharacterEncoding.UTF8);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleConvert = async () => {
    if (!input.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    const processingKey = `${mode}-${encoding}-${input.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      let result;
      if (mode === 'encode') {
        result = await Base64TextService.encode(input, encoding);
      } else {
        result = await Base64TextService.decode(input, encoding);
      }

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        setOutput(result.outputData as string);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.BASE64_TEXT);
        setError(errorMessage);
        setOutput('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleConvert();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setError(null);
      setIsLoading(false);
    }
  }, [input, mode, encoding]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="base64-text-encoder">
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>Base64 encoding requires JavaScript. Please enable JavaScript to use this tool.</p>
          <p>For command-line Base64 processing:</p>
          <pre><code># Encode
echo "Hello World" | base64

# Decode
echo "SGVsbG8gV29ybGQ=" | base64 -d</code></pre>
        </div>
      </noscript>

      <div style={{ marginBottom: '2rem' }}>
        <h1>Base64 Text Encoder/Decoder</h1>
        <p>
          Encode text to Base64 format or decode Base64 strings back to readable text.
          Supports UTF-8 encoding for international characters.
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>
            Mode:
          </label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            data-testid="mode-select"
          >
            <option value="encode">Encode</option>
            <option value="decode">Decode</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Character Encoding:
          </label>
          <select
            value={encoding}
            onChange={(e) => setEncoding(e.target.value as CharacterEncoding)}
          >
            <option value={CharacterEncoding.UTF8}>UTF-8 (Recommended)</option>
            <option value={CharacterEncoding.ASCII}>ASCII</option>
            <option value={CharacterEncoding.UTF16}>UTF-16</option>
            <option value={CharacterEncoding.LATIN1}>Latin-1</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            {mode === 'encode' ? 'Text Input:' : 'Base64 Input:'}
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={mode === 'encode' ? 'Enter text to encode...' : 'Enter Base64 to decode...'}
            data-testid="input-text"
          />
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>
            {mode === 'encode' ? 'Base64 Output:' : 'Decoded Text:'}
          </label>
          <textarea
            value={output}
            readOnly
            placeholder={isLoading ? 'Processing...' : 'Result will appear here...'}
            className={isLoading ? 'loading' : ''}
            data-testid="output-text"
          />
          {isLoading && (
            <div className="loading-indicator">Processing...</div>
          )}
        </div>

        <div className="button-container">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};